import {
  duplicatePairConverter,
  mergeRecordsResponseConverter,
  searchDuplicatesResponseConverter,
  searchMergeHistoryResponseConverter,
} from 'data/convertors/duplicateManagement.convertors';
import {
  TDismissDuplicateRequestUI,
  TDuplicatePairDB,
  TDuplicatePairUI,
  TMergeRecordsRequestUI,
  TMergeRecordsResponseDB,
  TMergeRecordsResponseUI,
  TRestoreMergeRequestUI,
  TRestoreMergeResponseUI,
  TSearchDuplicatesRequestUI,
  TSearchDuplicatesResponseDB,
  TSearchDuplicatesResponseUI,
  TSearchMergeHistoryRequestUI,
  TSearchMergeHistoryResponseDB,
  TSearchMergeHistoryResponseUI,
  TSwapRecordsRequestUI,
  TTriggerDetectionRequestUI,
  TTriggerDetectionResponseUI,
} from 'data/types/duplicateManagement.types';
import { EDuplicateManagementUrls } from 'data/urls/duplicateManagement.url';
import { authSplitApi } from 'redux/helpers/slice.helpers';

export const duplicateManagementApi = authSplitApi('duplicateManagement', [
  'duplicate-pairs',
  'merge-history',
]).injectEndpoints({
  endpoints: build => ({
    searchDuplicates: build.query<
      TSearchDuplicatesResponseUI,
      TSearchDuplicatesRequestUI
    >({
      query(body) {
        return {
          url: `${EDuplicateManagementUrls.BASE}/${EDuplicateManagementUrls.DUPLICATES_SEARCH}`,
          method: 'POST',
          body,
        };
      },
      providesTags: ['duplicate-pairs'],
      transformResponse: (data: TSearchDuplicatesResponseDB) =>
        searchDuplicatesResponseConverter.fromDb(data),
    }),
    triggerDetection: build.mutation<
      TTriggerDetectionResponseUI,
      TTriggerDetectionRequestUI
    >({
      query(body) {
        return {
          url: `${EDuplicateManagementUrls.BASE}/${EDuplicateManagementUrls.DUPLICATES_DETECT}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['duplicate-pairs'],
    }),
    mergeRecords: build.mutation<
      TMergeRecordsResponseUI,
      TMergeRecordsRequestUI
    >({
      query({ dryRun, ...body }) {
        return {
          url: `${EDuplicateManagementUrls.BASE}/${EDuplicateManagementUrls.MERGE}`,
          method: 'POST',
          params: dryRun ? { dryRun: true } : undefined,
          body,
        };
      },
      invalidatesTags: (_result, _error, arg) =>
        arg.dryRun ? [] : ['duplicate-pairs', 'merge-history'],
      transformResponse: (data: TMergeRecordsResponseDB) =>
        mergeRecordsResponseConverter.fromDb(data),
    }),
    swapDuplicatePair: build.mutation<TDuplicatePairUI, TSwapRecordsRequestUI>({
      query({ duplicatePairId, ...body }) {
        return {
          url: `${EDuplicateManagementUrls.BASE}/${EDuplicateManagementUrls.PAIRS}/${duplicatePairId}/${EDuplicateManagementUrls.SWAP}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['duplicate-pairs'],
      transformResponse: (data: TDuplicatePairDB) =>
        duplicatePairConverter.fromDb(data),
    }),
    dismissDuplicatePair: build.mutation<
      TDuplicatePairUI,
      TDismissDuplicateRequestUI
    >({
      query({ duplicatePairId, ...body }) {
        return {
          url: `${EDuplicateManagementUrls.BASE}/${EDuplicateManagementUrls.PAIRS}/${duplicatePairId}/${EDuplicateManagementUrls.DISMISS}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['duplicate-pairs'],
      transformResponse: (data: TDuplicatePairDB) =>
        duplicatePairConverter.fromDb(data),
    }),
    searchMergeHistory: build.query<
      TSearchMergeHistoryResponseUI,
      TSearchMergeHistoryRequestUI
    >({
      query(body) {
        return {
          url: `${EDuplicateManagementUrls.BASE}/${EDuplicateManagementUrls.MERGE_HISTORY_SEARCH}`,
          method: 'POST',
          body,
        };
      },
      providesTags: ['merge-history'],
      transformResponse: (data: TSearchMergeHistoryResponseDB) =>
        searchMergeHistoryResponseConverter.fromDb(data),
    }),
    restoreMerge: build.mutation<
      TRestoreMergeResponseUI,
      TRestoreMergeRequestUI
    >({
      query({ mergeHistoryId }) {
        return {
          url: `${EDuplicateManagementUrls.BASE}/${EDuplicateManagementUrls.MERGE}/${mergeHistoryId}/${EDuplicateManagementUrls.RESTORE}`,
          method: 'POST',
          headers: {
            'X-Confirm-Restore': 'true',
          },
        };
      },
      invalidatesTags: ['duplicate-pairs', 'merge-history'],
    }),
  }),
});

export const {
  useSearchDuplicatesQuery,
  useLazySearchDuplicatesQuery,
  useTriggerDetectionMutation,
  useMergeRecordsMutation,
  useSwapDuplicatePairMutation,
  useDismissDuplicatePairMutation,
  useSearchMergeHistoryQuery,
  useLazySearchMergeHistoryQuery,
  useRestoreMergeMutation,
} = duplicateManagementApi;
